/**
 * HTTP Client
 * Centralized fetch wrapper using Bearer JWT tokens.
 */

import { getAuthToken } from "../auth/authSession";
import { API_BASE_URL } from '../config/environment';

const DEFAULT_TIMEOUT_MS = 30000;
const RETRY_DELAY_MS = 500;
const RETRYABLE_STATUS_CODES = [502, 503, 504];

let onUnauthorized = null;

/** Register a callback that fires when an authenticated request receives a 401. */
export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

/**
 * Map an HTTP status code to a user-friendly error message.
 * Uses the backend's error message when available.
 */
const getHttpErrorMessage = (status, data) => {
  const backendMessage = data?.error?.message || data?.message;

  switch (status) {
    case 400:
      return backendMessage || 'Invalid request. Please check your input and try again.';
    case 401:
      return backendMessage || 'Incorrect email or password.';
    case 403:
      return backendMessage || 'You do not have permission to perform this action.';
    case 404:
      return backendMessage || 'The requested resource was not found.';
    case 409:
      return backendMessage || 'This record already exists.';
    case 422:
      return backendMessage || 'Validation failed. Please check your input.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
    case 502:
    case 503:
      return 'Server error. Please try again later or contact support.';
    default:
      return backendMessage || 'Something went wrong. Please try again.';
  }
};

const buildError = (message, statusCode, code) => ({
  error: { message, statusCode, code },
});

const parseResponseBody = async (response) => {
  if (response.status === 204) return {};

  const contentType = response.headers.get('content-type') || '';
  try {
    if (contentType.includes('json')) {
      return await response.json();
    }
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return { raw: text };
    }
  } catch {
    return {};
  }
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithTimeout = async (url, config, timeoutMs, callerSignal) => {
  const controller = new AbortController();
  let timedOut = false;

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onCallerAbort = () => controller.abort();
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }
  }

  try {
    const response = await fetch(url, { ...config, signal: controller.signal });
    return { response };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { abortReason: timedOut ? 'timeout' : 'aborted' };
    }
    return { error };
  } finally {
    clearTimeout(timeoutId);
    if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
  }
};

/**
 * Core request function.
 * @param {string} endpoint
 * @param {object} options
 * @param {string}      options.method
 * @param {object}      options.body
 * @param {boolean}     options.skipAuth
 * @param {object}      options.headers
 * @param {AbortSignal} options.signal
 * @param {number}      options.timeoutMs
 * @param {number}      options.retries
 */
export const apiClient = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    body,
    skipAuth = false,
    headers = {},
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries = method === 'GET' ? 1 : 0,
  } = options;

  const token = getAuthToken();

  const requestHeaders = {
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    ...(!skipAuth && token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  const config = {
    method,
    headers: requestHeaders,
    ...(body ? { body: JSON.stringify(body) } : {}),
  };

  const url = `${API_BASE_URL}${endpoint}`;
  const maxAttempts = 1 + Math.max(0, retries);

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const isLastAttempt = attempt === maxAttempts;
    const { response, abortReason, error } = await fetchWithTimeout(
      url,
      config,
      timeoutMs,
      signal,
    );

    if (abortReason === 'aborted') {
      return buildError('Request was cancelled.', 0, 'aborted');
    }

    if (abortReason === 'timeout') {
      if (!isLastAttempt) {
        await delay(RETRY_DELAY_MS * attempt);
        continue;
      }
      console.error(`[API Client] ${method} ${endpoint}: timed out after ${timeoutMs}ms`);
      return buildError(
        'The server is taking too long to respond. Please try again.',
        0,
        'timeout',
      );
    }

    if (error) {
      if (!isLastAttempt) {
        await delay(RETRY_DELAY_MS * attempt);
        continue;
      }
      // Network-level failure: offline, CORS, DNS
      const isNetworkError = error instanceof TypeError;
      console.error(`[API Client] ${method} ${endpoint}:`, error.message);
      return buildError(
        isNetworkError
          ? 'Unable to connect to the server. Please check your internet connection and try again.'
          : 'An unexpected error occurred. Please try again.',
        0,
        isNetworkError ? 'network' : 'unknown',
      );
    }

    const data = await parseResponseBody(response);

    if (!response.ok) {
      if (response.status === 401 && !skipAuth) {
        onUnauthorized?.();
        return buildError('Your session has expired. Please sign in again.', 401, 'http');
      }
      if (RETRYABLE_STATUS_CODES.includes(response.status) && !isLastAttempt) {
        await delay(RETRY_DELAY_MS * attempt);
        continue;
      }
      return buildError(getHttpErrorMessage(response.status, data), response.status, 'http');
    }

    return data;
  }

  return buildError('Something went wrong. Please try again.', 0, 'unknown');
};

export const get  = (endpoint, options = {}) => apiClient(endpoint, { ...options, method: 'GET' });
export const post = (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: 'POST', body });
export const put  = (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: 'PUT', body });
export const patch = (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: 'PATCH', body });
export const del  = (endpoint, options = {}) => apiClient(endpoint, { ...options, method: 'DELETE' });

export default apiClient;
